import { Link, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import clsx from "clsx";
import { useToast } from "../components/Toast";
import { api } from "../lib/api";

/**
 * Jahresabschluss-Prüfliste (eigene Seite seit 27.10.2026, User): immer aktuell berechnet. „Steuerpaket erstellen“ führt bei offenen Punkten
 * hierher. Jeder Punkt wird an seiner Stelle aufgelöst (Link) oder mit Notiz bestätigt; wer Punkte bewusst übergehen will, nimmt
 * „Steuerpaket trotzdem erstellen“ — das wird protokolliert (nur für den User, nicht im Paket).
 */
export type PListe = { jahr: number; offen: number; sperrt: boolean;
  punkte: { id: string; titel: string; status: "ok" | "offen" | "bestaetigt"; detail: string[]; bestaetigbar: boolean; ziel?: string;
    bestaetigung?: { von: string; am: number; notiz: string | null } }[];
  uebergangen?: { von: string; am: number; offen: { id: string; titel: string }[] }[] };

const datum = (s: number) => new Date(s * 1000).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });

export function PrueflistePage() {
  const [sp, setSp] = useSearchParams();
  const { data: jahre } = useQuery<{ jahr: number }[]>({ queryKey: ["buchungen-jahre"], queryFn: () => api.get("/api/buchungen/jahre") });
  const jahr = Number(sp.get("jahr")) || jahre?.[0]?.jahr || null;
  const vomPaket = sp.get("vomPaket") === "1";
  const { data } = useQuery<PListe>({ queryKey: ["pruefliste", jahr], queryFn: () => api.get(`/api/steuer/${jahr}/pruefliste`), enabled: jahr !== null });
  const [dialog, setDialog] = useState(false);
  const qc = useQueryClient();
  const offen = data?.punkte.filter((p) => p.status === "offen") ?? [];
  const Trotzdem = () => (data && data.sperrt
    ? <button type="button" onClick={() => setDialog(true)} className="px-3 py-1.5 rounded-ios bg-white shadow-card text-ios-blue text-[14px]">Steuerpaket trotzdem erstellen</button>
    : data && <a href={`/api/steuer/${data.jahr}/paket`} className="inline-block px-3 py-1.5 rounded-ios bg-ios-blue text-white text-[14px]">Steuerpaket erstellen</a>);
  return (
    <div className="max-w-3xl mx-auto p-4 lg:p-6 space-y-4">
      <div className="flex items-baseline gap-3">
        <h1 className="text-[22px] font-semibold tracking-tight flex-1">Prüfliste</h1>
        <select className="border border-ios-line rounded-lg px-2 py-1.5 text-[16px] lg:text-[14px] bg-white" value={jahr ?? ""} aria-label="Steuerjahr"
          onChange={(e) => setSp({ jahr: e.target.value }, { replace: true })}>
          {/* das gewählte Jahr immer anbieten, auch wenn es noch keine Buchungen hat (sonst zeigt die Auswahl ein anderes Jahr) */}
          {[...new Set([...(jahre ?? []).map((j) => j.jahr), ...(jahr ? [jahr] : [])])].sort((x, y) => y - x).map((j) => <option key={j} value={j}>{j}</option>)}
        </select>
      </div>
      {vomPaket && data && data.offen > 0 && (
        <p className="rounded-iosLg bg-ios-blue/10 text-ios-blue p-3 text-[14px]">
          Das Steuerpaket {data.jahr} ist noch nicht erstellt: {data.offen} Punkte sind offen. Löse sie hier auf — oder übergehe sie bewusst mit „Steuerpaket trotzdem erstellen“.
        </p>
      )}
      {!data ? <p className="text-[14px] text-ios-muted">Lädt …</p> : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <p className={clsx("text-[14px] flex-1", data.offen ? "text-ios-blue" : "text-ios-green")}>
              {data.offen ? `${data.offen} Punkte offen` : "Alle Punkte erledigt"}
              {!data.sperrt && data.offen > 0 && data.jahr < 2025 && " — nur zur Ansicht (2024 ist schon beim Steuerberater)."}
            </p>
            <Trotzdem />
          </div>
          <section className="bg-white rounded-iosLg shadow-card p-4">
            <ul className="divide-y divide-ios-line">
              {[...offen, ...data.punkte.filter((p) => p.status !== "offen")].map((p) => <PunktZeile key={p.id} p={p} jahr={data.jahr} />)}
            </ul>
          </section>
          {offen.length > 3 && <div className="flex justify-end"><Trotzdem /></div>}
          {(data.uebergangen ?? []).length > 0 && (
            <section className="bg-white rounded-iosLg shadow-card p-4 text-[13px]">
              <h2 className="text-[15px] font-medium mb-1">Bewusst übergangen</h2>
              {data.uebergangen!.map((u, i) => (
                <div key={i} className="py-1"><b>{datum(u.am)}</b> von {u.von}: {u.offen.length} offene Punkte — {u.offen.map((o) => o.titel).join(" · ")}</div>
              ))}
            </section>
          )}
        </>
      )}
      {dialog && data && (
        <Dialog open onClose={() => setDialog(false)} className="relative z-50">
          <div className="fixed inset-0 bg-black/30" aria-hidden />
          <div className="fixed inset-0 flex items-end md:items-center justify-center p-4">
            <DialogPanel className="w-full max-w-lg rounded-iosLg bg-white p-4 shadow-card text-[14px]">
              <DialogTitle className="text-[17px] font-semibold">Steuerpaket {data.jahr} trotzdem erstellen?</DialogTitle>
              <p className="mt-1 text-ios-muted">Diese {offen.length} Punkte sind noch offen. Das wird hier vermerkt (nur für dich, nicht im Paket):</p>
              <ul className="mt-2 list-disc ml-5 space-y-0.5">{offen.map((o) => <li key={o.id}>{o.titel}</li>)}</ul>
              <div className="mt-4 flex justify-end gap-2">
                <button type="button" onClick={() => setDialog(false)} className="px-3 py-1.5 rounded-ios bg-white shadow-card">Abbrechen</button>
                <a href={`/api/steuer/${data.jahr}/paket?trotzdem=1`} onClick={() => setTimeout(() => { setDialog(false); void qc.invalidateQueries({ queryKey: ["pruefliste", data.jahr] }); }, 1500)}
                  className="px-3 py-1.5 rounded-ios bg-ios-blue text-white">Trotzdem erstellen</a>
              </div>
            </DialogPanel>
          </div>
        </Dialog>
      )}
    </div>
  );
}

/** Ein Punkt: Status mit Symbol + Text (nie nur Farbe), Link zur Stelle, bestätigen mit Notiz. */
function PunktZeile({ p, jahr }: { p: PListe["punkte"][number]; jahr: number }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [notiz, setNotiz] = useState("");
  const aendern = useMutation({
    mutationFn: (an: boolean) => an ? api.post(`/api/steuer/${jahr}/pruefliste/${encodeURIComponent(p.id)}`, { notiz })
      : api.del(`/api/steuer/${jahr}/pruefliste/${encodeURIComponent(p.id)}`),
    onSuccess: () => { setNotiz(""); void qc.invalidateQueries({ queryKey: ["pruefliste", jahr] }); },
    onError: () => toast.error("Speichern fehlgeschlagen"),
  });
  return (
    <li className="py-2.5 flex gap-2 items-start text-[14px]">
      <span aria-hidden className={clsx("shrink-0 w-5 text-center", p.status === "offen" ? "text-ios-blue" : "text-ios-green")}>{p.status === "offen" ? "○" : "✓"}</span>
      <div className="flex-1 min-w-0 space-y-1">
        <div>{p.titel} <span className="sr-only">({p.status})</span></div>
        {p.detail.map((d) => <div key={d} className="text-[13px] text-ios-blue">{d}</div>)}
        {p.bestaetigung && <div className="text-[12px] text-ios-muted">bestätigt von {p.bestaetigung.von} am {datum(p.bestaetigung.am)}{p.bestaetigung.notiz ? ` — „${p.bestaetigung.notiz}“` : ""}</div>}
        <div className="flex flex-wrap items-center gap-2 text-[13px]">
          {p.ziel && p.status === "offen" && <Link to={p.ziel} className="text-ios-blue">zur Stelle →</Link>}
          {p.bestaetigbar && p.status !== "bestaetigt" && <>
            <input className="border border-ios-line rounded-lg px-2 py-1 text-[16px] lg:text-[13px] flex-1 min-w-[10rem]" placeholder="Notiz (optional)" value={notiz} onChange={(e) => setNotiz(e.target.value)} />
            <button type="button" disabled={aendern.isPending} onClick={() => aendern.mutate(true)} className="px-2 py-0.5 rounded-ios bg-ios-blue text-white">bestätigen</button>
          </>}
          {p.bestaetigbar && p.status === "bestaetigt" && <button type="button" disabled={aendern.isPending} onClick={() => aendern.mutate(false)} className="text-ios-muted">zurücknehmen</button>}
        </div>
      </div>
    </li>
  );
}
